import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { Eye, Filter } from 'lucide-react'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Card } from '../ui/card'
import LoadingSpinner from '../leevo_ui/LoadingSpinner/LoadingSpinner'
import { formatMessageTime } from '../../utilities/messageHelpers'

function ConversationReports() {
  const [reports, setReports] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('pending')

  useEffect(() => {
    fetchReports()
  }, [statusFilter])

  const fetchReports = async () => {
    setIsLoading(true)
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {}
      const response = await axios.get('/api/admin/conversation_reports', { params })
      setReports(response.data.data || [])
    } catch (error) {
      console.error('Error fetching reports:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const updateReportStatus = async (reportId, newStatus) => {
    try {
      await axios.put(`/api/admin/conversation_reports/${reportId}`, {
        status: newStatus
      })
      fetchReports()
    } catch (error) {
      console.error('Error updating report:', error)
    }
  }

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-500'
      case 'reviewed':
        return 'bg-blue-500'
      case 'resolved':
        return 'bg-green-500'
      default:
        return 'bg-gray-500'
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <LoadingSpinner size="1/5" color="purple" />
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Conversation Reports</h1>

        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="text-sm text-gray-600">Filter:</span>
          {['all', 'pending', 'reviewed', 'resolved'].map((status) => (
            <Button
              key={status}
              onClick={() => setStatusFilter(status)}
              variant={statusFilter === status ? 'default' : 'outline'}
              size="sm"
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      {reports.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-gray-500">No reports found</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {reports.map((report) => {
            const attrs = report.attributes || {}
            return (
              <Card key={report.id} className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Badge className={getStatusBadgeColor(attrs.status)}>{attrs.status}</Badge>
                    <span className="text-sm text-gray-500">Reported {formatMessageTime(attrs.createdAt)}</span>
                  </div>
                  <Button
                    onClick={() =>
                      (window.location.href = `/admin/conversations/${report.relationships?.conversation?.data?.id}/transcript`)
                    }
                    variant="outline"
                    size="sm"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View Conversation
                  </Button>
                </div>

                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-1">Reason:</p>
                  <p className="text-sm text-gray-900">{attrs.reason}</p>
                </div>

                {attrs.status === 'pending' && (
                  <div className="flex gap-2">
                    <Button onClick={() => updateReportStatus(report.id, 'reviewed')} variant="outline" size="sm">
                      Mark as Reviewed
                    </Button>
                    <Button
                      onClick={() => updateReportStatus(report.id, 'resolved')}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                    >
                      Mark as Resolved
                    </Button>
                  </div>
                )}

                {attrs.status === 'reviewed' && (
                  <Button
                    onClick={() => updateReportStatus(report.id, 'resolved')}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Mark as Resolved
                  </Button>
                )}
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default ConversationReports
